import React, { useState } from 'react';
import { Plus, Pencil, Trash2, ChevronUp, ChevronDown } from 'lucide-react';
import { api } from '../../lib/api.js';
import { Field, Text, Area, FilePicker, UploadButton } from './fields.jsx';
import { Empty } from '../../components/states.jsx';

const blank = () => ({ id: null, title: '', description: '', url: '' });

export default function MaterialsAdmin({ year, files, notify, reload, reloadFiles }) {
  const [draft, setDraft] = useState(null);
  const [busy, setBusy] = useState(false);

  if (!year) return null;
  const materials = year.materials || [];
  const set = (patch) => setDraft((current) => ({ ...current, ...patch }));

  const save = async (event) => {
    event.preventDefault();
    if (!draft.title.trim()) return notify('Vyplňte název materiálu.', 'error');
    if (!draft.url.trim()) return notify('Vyplňte odkaz.', 'error');

    setBusy(true);
    const payload = { yearId: year.id, title: draft.title, description: draft.description, url: draft.url };
    try {
      if (draft.id) await api.admin.updateMaterial(draft.id, payload);
      else await api.admin.createMaterial(payload);
      await reload();
      setDraft(null);
      notify(draft.id ? 'Materiál uložen.' : 'Materiál přidán.');
    } catch (error) {
      notify(error.message, 'error');
    } finally {
      setBusy(false);
    }
  };

  const remove = async (material) => {
    if (!window.confirm(`Opravdu smazat „${material.title}"?`)) return;
    try {
      await api.admin.deleteMaterial(material.id);
      await reload();
      notify('Materiál smazán.');
    } catch (error) {
      notify(error.message, 'error');
    }
  };

  const move = async (index, delta) => {
    const next = [...materials];
    const target = index + delta;
    if (target < 0 || target >= next.length) return;
    [next[index], next[target]] = [next[target], next[index]];
    try {
      await api.admin.reorder('materials', next.map((m) => m.id));
      await reload();
    } catch (error) {
      notify(error.message, 'error');
    }
  };

  return (
    <>
      <div className="admin-title">
        <div>
          <h1>Materiály</h1>
          <p>Ročník {year.label} · {materials.length} položek</p>
        </div>
        {!draft && (
          <button type="button" className="btn btn-primary" onClick={() => setDraft(blank())}>
            <Plus size={16} /> Přidat materiál
          </button>
        )}
      </div>

      {draft && (
        <form className="editor" onSubmit={save} style={{ marginBottom: '1.5rem' }}>
          <h2>{draft.id ? 'Úprava materiálu' : 'Nový materiál'}</h2>

          <Field label="Název" id="nazev-materialu">
            <Text id="nazev-materialu" value={draft.title} onChange={(v) => set({ title: v })} autoFocus />
          </Field>

          <Field label="Popis" id="popis-materialu" hint="Krátká věta, k čemu materiál je.">
            <Area id="popis-materialu" value={draft.description} onChange={(v) => set({ description: v })} rows={2} />
          </Field>

          <Field label="Odkaz" id="odkaz-materialu">
            <div style={{ display: 'flex', gap: '.4rem' }}>
              <Text id="odkaz-materialu" value={draft.url} onChange={(v) => set({ url: v })} placeholder="https://… nebo /uploads/…" />
              <FilePicker files={files} onNeedFiles={reloadFiles} onPick={(url) => set({ url })} />
              <UploadButton notify={notify} onNeedFiles={reloadFiles} onUploaded={(file) => set({ url: file.url })} />
            </div>
          </Field>

          <div className="form-actions">
            <button type="submit" className="btn btn-primary" disabled={busy}>{busy ? 'Ukládám…' : 'Uložit'}</button>
            <button type="button" className="btn btn-ghost" onClick={() => setDraft(null)}>Zrušit</button>
          </div>
        </form>
      )}

      {materials.length === 0 && !draft ? (
        <Empty title="Zatím žádné materiály">Přidejte první materiál tlačítkem nahoře.</Empty>
      ) : (
        <div className="stack-tight">
          {materials.map((material, index) => (
            <div className="admin-row" key={material.id}>
              <div className="admin-row-order">
                <button type="button" className="icon-btn" onClick={() => move(index, -1)} disabled={index === 0} aria-label="Posunout nahoru">
                  <ChevronUp size={13} />
                </button>
                <button type="button" className="icon-btn" onClick={() => move(index, 1)} disabled={index === materials.length - 1} aria-label="Posunout dolů">
                  <ChevronDown size={13} />
                </button>
              </div>

              <div className="admin-row-main">
                <div className="admin-row-title">{material.title}</div>
                <div className="admin-row-sub"><span className="file-url">{material.url}</span></div>
              </div>

              <div className="admin-row-actions">
                <button type="button" className="icon-btn" onClick={() => setDraft({ ...material })} aria-label={`Upravit ${material.title}`}>
                  <Pencil size={15} />
                </button>
                <button type="button" className="icon-btn icon-btn-danger" onClick={() => remove(material)} aria-label={`Smazat ${material.title}`}>
                  <Trash2 size={15} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </>
  );
}
